import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, NOTIFICATION_CATEGORIES } from '../contexts/NotificationContext';
import { BackOfficeNotificationItem, NotificationCategory } from '../domain/payload/response/backOfficeNotification/BackOfficeNotificationResp';
import '../styles/notification.scss';

// Direct color mapping (avoids Tailwind dependency)
const CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  MONITORING: { bg: '#dbeafe', color: '#2563eb' },
  ALERT: { bg: '#fef3c7', color: '#d97706' },
  SYSTEM: { bg: '#f3e8ff', color: '#7c3aed' },
  CAMPAIGN: { bg: '#fce7f3', color: '#db2777' },
  WELCOME: { bg: '#d1fae5', color: '#059669' },
  ANNOUNCEMENT: { bg: '#cffafe', color: '#0891b2' },
};

const NotificationDropdown: React.FC = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    total,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    fetchUnreadCount,
    formatRelativeTime,
    openPermissionModal,
  } = useNotifications();

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Fetch fresh data when dropdown opens
  const handleToggle = useCallback(() => {
    const opening = !isOpen;
    setIsOpen(opening);
    if (opening) {
      setFilter('all');
      fetchNotifications({ page: 1, size: 5 });
      fetchUnreadCount();
      // Buka modal custom permission jika izin notifikasi belum diputuskan
      if ('Notification' in window && Notification.permission === 'default') {
        openPermissionModal();
      }
    }
  }, [isOpen, fetchNotifications, fetchUnreadCount, openPermissionModal]);

  const handleNotificationClick = useCallback(
    (notification: BackOfficeNotificationItem) => {
      if (!notification.isRead) {
        markAsRead(notification.id);
      }

      if (notification.actionValue === '/dashboard') {
        navigate('/dashboard', {
          state: { payloadUrl: notification.payload?.url ?? null },
        });
      } else if (notification.actionValue) {
        navigate(notification.actionValue);
      }

      setIsOpen(false);
    },
    [markAsRead, navigate]
  );

  const handleMarkAllAsRead = useCallback(async () => {
    await markAllAsRead();
    await fetchUnreadCount();
    await fetchNotifications({ page: 1, size: 5 });
  }, [markAllAsRead, fetchUnreadCount, fetchNotifications]);

  const handleViewAll = useCallback(() => {
    navigate('/notifications');
    setIsOpen(false);
  }, [navigate]);

  const hasUnread = unreadCount > 0 || notifications.some((n) => !n.isRead);

  const filteredNotifications =
    filter === 'all'
      ? notifications
      : notifications.filter((n) => !n.isRead);

  const getCategoryStyle = (category: string) => {
    return CATEGORY_COLORS[category] || CATEGORY_COLORS.MONITORING;
  };

  const getCategoryIcon = (category: string) => {
    const meta = NOTIFICATION_CATEGORIES[category as NotificationCategory];
    return meta?.icon || 'fa-solid fa-bell';
  };

  return (
    <div className="notif-dropdown-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className="notif-bell-btn"
        onClick={handleToggle}
        title="Notifications"
        aria-label="Notifications"
      >
        <i className="fa-solid fa-bell" />
        {unreadCount > 0 && (
          <span className="notif-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notif-dropdown-panel">
          {/* Header */}
          <div className="notif-dropdown-header">
            <div className="d-flex align-items-center gap-2">
              <span className="notif-dropdown-title">Notifications</span>
              {hasUnread && (
                <span className="notif-unread-pill">
                  {unreadCount > 0 ? `${unreadCount} Unread` : 'Unread'}
                </span>
              )}
            </div>
            {hasUnread && (
              <button className="notif-mark-all-btn" onClick={handleMarkAllAsRead}>
                <i className="fa-solid fa-check-double" /> Mark all as read
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="notif-tabs">
            <button
              className={`notif-tab ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All <span className="notif-tab-count">{total}</span>
            </button>
            <button
              className={`notif-tab ${filter === 'unread' ? 'active' : ''}`}
              onClick={() => setFilter('unread')}
            >
              Unread <span className="notif-tab-count">{unreadCount}</span>
            </button>
          </div>

          {/* Notification List */}
          <div className="notif-dropdown-list">
            {loading ? (
              <div className="notif-loading">
                <div
                  className="spinner-border spinner-border-sm text-primary"
                  role="status"
                />
              </div>
            ) : filteredNotifications.length > 0 ? (
              filteredNotifications.slice(0, 5).map((n) => {
                const catStyle = getCategoryStyle(n.category);
                const catIcon = getCategoryIcon(n.category);
                return (
                  <div
                    key={n.id}
                    className={`notif-dropdown-item ${!n.isRead ? 'unread' : ''}`}
                    onClick={() => handleNotificationClick(n)}
                    role="button"
                    tabIndex={0}
                  >
                    <div
                      className="notif-icon-circle"
                      style={{ backgroundColor: catStyle.bg }}
                    >
                      <i className={catIcon} style={{ color: catStyle.color }} />
                    </div>
                    <div className="notif-item-content">
                      <div className="notif-item-header">
                        <span className="notif-item-title">{n.title}</span>
                        <span className="notif-item-time">
                          {n.timeAgo || formatRelativeTime(n.createdAt)}
                        </span>
                      </div>
                      <p className="notif-item-message">{n.message}</p>
                    </div>
                    {!n.isRead && <span className="notif-unread-dot" />}
                  </div>
                );
              })
            ) : (
              <div className="notif-empty">
                <i className="fa-regular fa-bell-slash" />
                <span>No notifications</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="notif-dropdown-footer">
            <button onClick={handleViewAll}>
              View all notifications <i className="fa-solid fa-arrow-right" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
